
import { useRef } from "react";
import { X, Pipette } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface ColorPickerProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  error?: boolean;
  "aria-describedby"?: string;
}

export function ColorPicker({
  value,
  onChange,
  id,
  disabled,
  error,
  "aria-describedby": ariaDescribedBy,
}: ColorPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.toUpperCase());
  };

  const handleClear = () => {
    onChange("");
  };

  const handleSwatchClick = () => {
    inputRef.current?.click();
  };

  const hasValue = value && /^#[0-9A-Fa-f]{6}$/i.test(value);

  return (
    <div className="flex gap-2 items-center">
      <input
        ref={inputRef}
        type="color"
        id={id}
        value={hasValue ? value : "#000000"}
        onChange={handleColorChange}
        disabled={disabled}
        className="sr-only"
        aria-describedby={ariaDescribedBy}
      />
      <button
        type="button"
        onClick={handleSwatchClick}
        disabled={disabled}
        className={cn(
          "h-9 w-9 rounded border shrink-0 transition-colors cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-destructive",
          !hasValue && "bg-muted"
        )}
        style={hasValue ? { backgroundColor: value } : undefined}
        aria-label={hasValue ? `Selected color: ${value}` : "Select a color"}
        aria-invalid={error}
      >
        {!hasValue && (
          <span className="flex items-center justify-center text-muted-foreground">
            <Pipette className="h-4 w-4" />
          </span>
        )}
      </button>
      {hasValue && (
        <>
          <span className="text-sm font-mono text-muted-foreground min-w-[70px]">
            {value}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            disabled={disabled}
            className="h-8 w-8 shrink-0"
            aria-label="Clear color"
          >
            <X className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
